import { Button, ConfigProvider, Flex } from '@/components';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import { useProject } from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import { t } from '@/i18n';
import { CommentOutlined, PlusOutlined } from '@ant-design/icons';
import { isEqual } from 'lodash-es';
import {
  type CSSProperties,
  type FC,
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router';
import { useCommentList } from '../../hooks/comment';
import type { PinpointMappingType } from '../../models/pinpoint';
import type { CommentStackSiderViewConfigType } from '../../types/stack-sider-view';
import { generateDataPinpointAttributes } from '../../utils/generate-data-pinpoint-attributes';
import { generatePinpointKey } from '../../utils/generate-pinpoint-key';
import { isMatchPinpoint, isNotUsePinpoint } from '../../utils/pinpoint';
import { usePinpoint } from './PinpointProvider';

type Props = {
  pinpoint?: PinpointMappingType;
  objectAssigneeIds?: string[] | null;
  noHover?: boolean;
  noRelative?: boolean;
  useDynamicHeight?: boolean; // To keep height of root div when its in td element
  wrapperStyle?: CSSProperties;
};

export const PinpointMarker: FC<PropsWithChildren<Props>> = ({
  pinpoint,
  children,
  ...restProps
}) => {
  const { id } = useId();
  const { data: project } = useProject(id);

  const { notUsePinpoint } = isNotUsePinpoint(project);

  return !notUsePinpoint && pinpoint ? (
    <BasePinpointMarker
      {...restProps}
      pinpoint={pinpoint}
      objectAssigneeIds={project?.peopleInChargeIds}
      filteredOrganizationFiscalYear={project?.fiscalYear}
    >
      {children}
    </BasePinpointMarker>
  ) : (
    <>{children}</>
  );
};

const BasePinpointMarker: FC<
  PropsWithChildren<
    Omit<Props, 'pinpoint'> & {
      filteredOrganizationFiscalYear: number | undefined;
      pinpoint: PinpointMappingType;
    }
  >
> = ({
  pinpoint,
  objectAssigneeIds,
  children,
  noHover,
  noRelative,
  useDynamicHeight,
  wrapperStyle,
  filteredOrganizationFiscalYear,
}) => {
  const {
    pinpoint: selectedPinpoint,
    resetPinpoint,
    getPinpointScrollTargetRef,
  } = usePinpoint();
  const [hover, setHover] = useState(false);
  const [parentElementHeight, setParentElementHeight] = useState<
    number | undefined
  >(undefined);
  const padding = 10;

  const commentStackSiderView = useCommentStackSiderView();

  const enablePinpoint = commentStackSiderView.list.length > 0;

  const { id } = useId();
  //IMO: 箇所指定の数分リクエストが発生してしまうので、pinpointを全検取得するAPIが必要
  const commentList = useCommentList({
    params: { pinpointKey: generatePinpointKey(pinpoint?.field) },
    select: (d) => d.results,
    enabled: !!pinpoint && enablePinpoint,
    retry: false,
  });

  const isSelectedPinpoint = useMemo<boolean>(() => {
    if (!selectedPinpoint) {
      return false;
    }
    return isMatchPinpoint({
      targetPinpoint: selectedPinpoint,
      sourcePinpoint: pinpoint,
    });
  }, [pinpoint, selectedPinpoint]);

  const [searchParams] = useSearchParams();
  const commentId = searchParams.get('commentId');
  const pinpointMatchByCommentId = useMemo(() => {
    const target = commentList.data?.find((thread) => {
      return commentId === thread.id && isEqual(thread.pinpoint, pinpoint);
    })?.pinpoint;
    return target;
  }, [commentList.data, commentId, pinpoint]);

  const isSelected = Boolean(pinpointMatchByCommentId || isSelectedPinpoint);

  const hasComment = useMemo(() => {
    return commentList.data?.some((comment) => {
      const commentPinpoint = comment.pinpoint;
      const markerPinpoint = pinpoint;
      if (!commentPinpoint) {
        return false;
      }
      return isMatchPinpoint({
        targetPinpoint: commentPinpoint,
        sourcePinpoint: markerPinpoint,
      });
    });
  }, [commentList.data, pinpoint]);

  const commentConfigTarget = {
    projectFiscalYear: filteredOrganizationFiscalYear,
    objectAssigneeIds,
  } satisfies CommentStackSiderViewConfigType['target'];

  const pinpointRef = useRef<HTMLDivElement>(null);
  const parentElement = useDynamicHeight && pinpointRef.current?.parentElement;

  useEffect(() => {
    if (!parentElement) {
      return;
    }

    const updatedParentElementHeight = () => {
      setParentElementHeight(parentElement.scrollHeight - padding * 2 * 2);
    };

    const observer = new ResizeObserver(updatedParentElementHeight);
    observer.observe(parentElement);

    updatedParentElementHeight();

    return () => {
      observer.disconnect();
    };
  }, [parentElement]);

  return (
    <div
      data-pinpoint={generateDataPinpointAttributes(pinpoint)}
      ref={pinpointRef}
      style={{
        boxSizing: 'border-box',
        ...wrapperStyle,
        ...(!noRelative && { position: 'relative' }),
        height: wrapperStyle?.height ?? parentElementHeight,
      }}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
    >
      <div ref={getPinpointScrollTargetRef(pinpoint)} />
      {enablePinpoint && (
        <div
          style={{
            position: 'absolute',
            top: -padding,
            left: -padding,
            width: `calc(100% + ${padding * 2}px)`,
            height: `calc(100% + ${padding * 2}px)`,
            borderRadius: 2,
            transition: 'background-color 0.1s ease',
            ...(isSelected && !noHover && markedStyles),
            ...(hover && !noHover && markedStyles),
            zIndex: 10,
            pointerEvents: 'none',
          }}
        >
          <Flex
            style={{
              position: 'absolute',
              zIndex: 11,
              top: noRelative ? 16 : 4,
              right: noRelative ? 16 : 4,
              pointerEvents: 'auto',
            }}
            gap={2}
          >
            {hover && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();

                  const config = {
                    viewType: 'comment-form',
                    pinpoint,
                    isEditing: false,
                    target: commentConfigTarget,
                  } satisfies CommentStackSiderViewConfigType;

                  if (
                    commentStackSiderView.lastItem?.config.viewType ===
                      'comment-form' ||
                    commentStackSiderView.lastItem?.config.viewType ===
                      'comment-details'
                  ) {
                    commentStackSiderView.updateLastItem({ config });
                  } else {
                    commentStackSiderView.push({
                      payload: { config },
                      target: { type: 'project', projectId: id },
                    });
                  }

                  resetPinpoint();
                }}
              >
                {t('comment')}
              </Button>
            )}
            {hasComment && (
              <ConfigProvider
                theme={{
                  components: {
                    Button: hover
                      ? {}
                      : {
                          defaultBg: '#2484641F',
                          defaultColor: '#248464',
                          defaultBorderColor: 'transparent',
                        },
                  },
                }}
              >
                <Button
                  type={hover ? 'primary' : 'default'}
                  icon={<CommentOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    commentStackSiderView.set({
                      payload: [
                        {
                          config: {
                            viewType: 'comment-list',
                            pinpointKey: generatePinpointKey(pinpoint.field),
                            pinpoint: pinpoint,
                            target: commentConfigTarget,
                          },
                          siderType: 'comment',
                        },
                      ],
                      target: {
                        type: 'project',
                        projectId: id,
                      },
                    });
                  }}
                />
              </ConfigProvider>
            )}
          </Flex>
        </div>
      )}
      {children}
    </div>
  );
};

const markedStyles = {
  backgroundColor: 'rgba(36, 132, 100, 0.12)',
};
